import type { LexicalText } from "@/schema"
import { ProjectsResponse } from "@/schema/project"
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { getFormFieldsRequirementFromError } from "@/components/projects/form-fields-requirement"

import {
  checkProjectCompletion,
  completeProject,
  completeProjectMilestone,
  createProject,
  deleteProject,
  moveProjectToNextPhase,
  reopenProject,
  updateProject,
  updateProjectMilestone,
  updateProjectProgress,
  upsertFormFieldResponses,
} from "./api"

function setProjectInListCache<T extends { id: string }>(queryClient: QueryClient, data: T) {
  queryClient.setQueryData(["projects"], (oldData: unknown) => {
    if (!oldData || typeof oldData !== "object" || !("projects" in oldData)) return oldData
    const typedData = oldData as { projects: Array<{ id: string }> }
    return {
      ...oldData,
      projects: typedData.projects.map((project) => (project.id === data.id ? data : project)),
    }
  })
}

function invalidateProjectViewCounts(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["data-views", "project"] })
}

function invalidateCompletionQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["projects"] })
  queryClient.invalidateQueries({ queryKey: ["tasks"] })
  queryClient.invalidateQueries({ queryKey: ["households"] })
  queryClient.invalidateQueries({ queryKey: ["project-template"] })
  queryClient.invalidateQueries({ queryKey: ["project-templates"] })
  invalidateProjectViewCounts(queryClient)
}

export function useCreateProject(bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: Parameters<typeof createProject>[1]) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createProject(context, input)
    },
    meta: { errorToast: { fallback: "Failed to create project" } },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["household", data.household_id] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["project-template"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      invalidateProjectViewCounts(queryClient)
      if (data.projectable_type === "Contact" && data.projectable_id) {
        queryClient.invalidateQueries({ queryKey: ["contact", data.projectable_id, "projects"] })
      }
      if (data.projectable_type === "Business" && data.projectable_id) {
        queryClient.invalidateQueries({ queryKey: ["business", data.projectable_id, "projects"] })
      }
    },
  })
}

export function useUpdateProject() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      input,
      reassignTasks,
    }: {
      id: string
      input: Parameters<typeof updateProject>[2]
      reassignTasks?: boolean
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateProject(context, id, input, reassignTasks)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.setQueryData(["project", data.id], data)
    },
  })
}

export function useMoveProjectToNextPhase() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return moveProjectToNextPhase(context, id)
    },
    onSuccess: (data) => {
      // Update the specific project data immediately
      queryClient.setQueryData(["project", data.id], data)

      // Update projects list with new data
      setProjectInListCache(queryClient, data)

      // Invalidate related queries for fresh data
      invalidateCompletionQueries(queryClient)
    },
  })
}

export function useCompleteProjectMilestone(bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      outcomeId,
      projectComment,
      markAllTasksComplete,
      formResponses,
    }: {
      id: string
      outcomeId?: string
      projectComment?: LexicalText
      markAllTasksComplete?: boolean
      formResponses?: Record<string, string | number | boolean | null>
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return completeProjectMilestone(context, id, {
        ...(outcomeId && { outcome_id: outcomeId }),
        ...(projectComment && { project_comment: projectComment }),
        ...(markAllTasksComplete && { mark_all_tasks_complete: markAllTasksComplete }),
        ...(formResponses && { form_responses: formResponses }),
      })
    },
    meta: { errorToast: { fallback: "Failed to complete milestone" } },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["household", data.household_id] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      await queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.setQueryData(["project", data.id], data)
      setProjectInListCache(queryClient, data)
      invalidateCompletionQueries(queryClient)
    },
  })
}

export function useCheckProjectCompletion() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return checkProjectCompletion(context, id)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.setQueryData(["project", data.id], data)
      invalidateProjectViewCounts(queryClient)
    },
  })
}

export function useUpdateProjectProgress() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateProjectProgress(context, id)
    },
    onSuccess: (data) => {
      // Update the specific project data immediately
      queryClient.setQueryData(["project", data.id], data)

      // Update projects list with new data
      setProjectInListCache(queryClient, data)
    },
  })
}

export function useUpdateProjectMilestone(bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      milestoneId,
      formResponses,
    }: {
      id: string
      milestoneId: string
      formResponses?: Record<string, string | number | boolean | null>
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateProjectMilestone(context, id, milestoneId, formResponses)
    },
    meta: { errorToast: (error) => (getFormFieldsRequirementFromError(error) ? null : error.message) },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["household", data.household_id] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.setQueryData(["project", data.id], data)

      setProjectInListCache(queryClient, data)

      queryClient.invalidateQueries({ queryKey: ["projects", "by-template"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      invalidateProjectViewCounts(queryClient)
    },
  })
}

export function useCompleteProject(bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, markAllTasksComplete }: { id: string; markAllTasksComplete: boolean }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return completeProject(context, id, markAllTasksComplete)
    },
    meta: { errorToast: { fallback: "Failed to complete project" } },
    onSuccess: (data) => {
      queryClient.setQueryData(["project", data.id], data)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.invalidateQueries({ queryKey: ["project-template"] })
      invalidateProjectViewCounts(queryClient)
    },
  })
}

export function useReopenProject(bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return reopenProject(context, id)
    },
    meta: { errorToast: { fallback: "Failed to reopen project" } },
    onSuccess: (data) => {
      queryClient.setQueryData(["project", data.id], data)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["project-template"] })
      queryClient.invalidateQueries({ queryKey: ["project-templates"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      if (data.projectable_type === "Contact" && data.projectable_id) {
        queryClient.invalidateQueries({ queryKey: ["contact", data.projectable_id, "projects"] })
      }
      if (data.projectable_type === "Business" && data.projectable_id) {
        queryClient.invalidateQueries({ queryKey: ["business", data.projectable_id, "projects"] })
      }
      invalidateProjectViewCounts(queryClient)
    },
  })
}

export function useUpsertFormFieldResponses() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      responses,
    }: {
      projectId: string
      responses: Record<string, string | number | boolean | null>
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return upsertFormFieldResponses(context, projectId, responses)
    },
    meta: { errorToast: { fallback: "Failed to update form responses" } },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["household", data.household_id] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.setQueryData(["project", data.id], data)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
    },
  })
}

export function useDeleteProject(bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, destroyAllTasks }: { id: string; destroyAllTasks: boolean }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteProject(context, id, destroyAllTasks)
    },
    meta: { errorToast: { fallback: "Failed to delete project" } },
    onSuccess: (_, { id }) => {
      queryClient.removeQueries({ queryKey: ["project", id] })

      queryClient.setQueryData(["projects"], (oldData: ProjectsResponse | undefined) => {
        if (!oldData?.projects) return oldData
        return {
          ...oldData,
          projects: oldData.projects.filter((project) => project.id !== id),
        }
      })

      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["project-template"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      invalidateProjectViewCounts(queryClient)
    },
  })
}
